import React, { useState, useEffect } from "react";
import ReusableTable from "../../components/Employee/Table";
import {
  confirmDeletePopup,
  formatDateOnly,
  openAllowanceTypeModal,
  openDepartmentModal,
  successPopup,
} from "../../util/common";
import TextInput from "../../components/Reusable/TextInput";
import SelectInput from "../../components/Reusable/SelectInput";
import DateInput from "../../components/Reusable/DateInput";
import { useNavigate } from "react-router-dom";
import { FaEdit, FaTrash } from "react-icons/fa";
import Swal from "sweetalert2";
import "./sweetalert.css";
import { toast } from "react-toastify";
import { _useGetAllShiftSchedules } from "../../hooks/useShiftSchedules";
import { useQueryClient } from "@tanstack/react-query";
import {
  _useGetDepartmentById,
  useCreateDepartment,
  useDeleteDepartment,
  useGetAllDepartmentsPaginated,
  useGetDepartmentById,
  useUpdateDepartment,
} from "../../hooks/useDepartments";

function DepartmentListLayout() {
  const [pageCount, setPageCount] = useState(1);
  const navigate = useNavigate();

  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 20,
  });

  const [filter, setFilter] = useState({
    filterField1: "Name",
    filterValue1: null,
  });

  const handleOnPageChange = (pageIndex, pageSize) => {
    setPagination({
      page: pageIndex + 1,
      pageSize,
    });
  };

  const [departmentRecords, setDepartmentRecords] = useState([]);

  const {
    data: departmentData,
    isLoading: departmentIsLoading,
    isError: departmentIsError,
  } = useGetAllDepartmentsPaginated({
    page: pagination.page,
    pageSize: pagination.pageSize,
    filterField1: filter.filterField1,
    filterValue1: filter.filterValue1,
  });

  const deleteDepartment = useDeleteDepartment();
  const createDepartment = useCreateDepartment();
  const getDepartmentById = _useGetDepartmentById();
  const updateDepartment = useUpdateDepartment();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (departmentData && departmentData.data) {
      setDepartmentRecords(departmentData.data.data);
      setPageCount(departmentData.data.pagination.totalPages);
    }
  }, departmentData);

  const handleDelete = async (department) => {
    const isConfirmed = await confirmDeletePopup({
      text: `Are you sure you want to delete ${department.Name}?`,
    });

    if (isConfirmed) {
      deleteDepartment.mutate(department.id, {
        onSuccess: (data) => {
          queryClient.invalidateQueries({
            queryKey: [
              "get-all-department",
              pagination.page,
              pagination.pageSize,
              pagination.filterField1,
              pagination.filterValue1,
              pagination.filterField2,
              pagination.filterValue2,
            ],
          });
          successPopup({ text: "Work Location has been deleted." });
        },
      });
    }
  };

  const handleEdit = (department) => {
    if (!department) return;

    getDepartmentById.mutate(department.id, {
      onSuccess: async (data) => {
        if (!data?.success) return;

        const { id, Name, Status } = data.data;

        const result = await openDepartmentModal({
          id,
          name: Name,
          status: Status,
        });

        if (!result) return;

        updateDepartment.mutate(
          {
            id: result.id,
            payload: {
              Name: result.department,
              Status: true,
            },
          },
          {
            onSuccess: (res) => {
              if (res?.success) {
                toast.success(res.message);
              }
            },
            onError: (err) => {
              toast.error(err.response?.data?.message || "Error occurred");
            },
          }
        );
      },
    });
  };

  const handleAddDepartment = async () => {
    const result = await openDepartmentModal({
      id: "",
      name: "",
      status: true,
    });

    if (!result) return;

    createDepartment.mutate(
      {
        Name: result.department,
        Status: true,
      },
      {
        onSuccess: (data) => {
          if (data?.success) {
            toast.success(data.message);
          }
        },
        onError: (err) => {
          toast.error(err.response?.data?.message || "Error occurred");
        },
      }
    );
  };

  const tableColumns = [
    { accessorKey: "Name", header: "Name" },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const department = row.original;

        return (
          <div style={{ display: "flex", gap: "10px" }}>
            <FaEdit
              title="Edit"
              style={{ cursor: "pointer" }}
              onClick={() => handleEdit(department)}
            />

            <FaTrash
              title="Delete"
              style={{ cursor: "pointer", color: "red" }}
              onClick={() => handleDelete(department)}
            />
          </div>
        );
      },
    },
  ];

  return (
    <div className="overtime-form-container">
      <h2>Departments List</h2>
      <form className="overtime-form">
        <div className="form-column">
          <TextInput
            label="Name"
            placeholder={"My Department"}
            onChange={(val) => {
              setFilter((prev) => ({
                ...prev,
                filterValue1: val,
              }));
            }}
            value={filter.filterValue1}
          />
        </div>

        <div className="form-group full-width file-upload-section">
          <div className="attendance-filters">
            <div className="action-btns">
              <button
                className="overtime-btn"
                onClick={handleAddDepartment}
                type="button"
              >
                + Add Department
              </button>
            </div>
            <div className="attendance-records">
              <ReusableTable
                data={departmentRecords}
                columns={tableColumns}
                mode="server"
                initialPageSize={pagination.pageSize}
                onPageChange={handleOnPageChange}
                pageCount={pageCount}
                page={pagination.page - 1}
                pageSize={pagination.pageSize}
              />
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}

export default DepartmentListLayout;
